import React, { FC, useState } from 'react';
import {
  CaseNote,
  EconsultProviderTypes,
  EconsultUserRoles,
  NoteStatusCodes,
  NoteTypes
} from '@oh-vcp/components-common';
import { useEconsultCaseDetailsContext } from '@oh-vcp/components-web/src/econsultCaseDetailsContext/EconsultCaseDetailsContext';
import { useEffect } from 'react';
import useCaseDetails from '@oh-vcp/components-web/src/services/useCaseDetails';
import EconsultCaseConsultDetailsView from '../econsultCaseConsultDetailsView/EconsultCaseConsultDetailsView';
import {
  CaseDetails,
  FhoProfile,
  FhoProfileContext,
  NoteActionTypes
} from '@oh-vcp/components-common';
import EconsultCaseDraftDetailsView from '../econsultCaseDraftDetailsView/EconsultCaseDraftDetailsView';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DisplayMobile, LoadingInProgress, useNotifierContext } from '@oh-vcp/components-ui';
import { Box } from '@mui/material';
import useAssignerPrograms from '@oh-vcp/components-web/src/services/useAssignerPrograms';
import { useTranslation } from 'react-i18next';
import CaseService from '@oh-vcp/components-web/src/services/caseService';
import {
  hasCaseChanged,
  hasNoteChanged
} from '@oh-vcp/components-common/src/utils/econsultCaseUtils';
import { useQueryClient } from 'react-query';
import EconsultDraftLostWarningDialog from '@oh-vcp/components-web/src/econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';
import useCaseSearch from '../../api/useCaseSearch';

interface EconsultCaseDetailsViewProps {
  setUpdatedCaseDetails: (updCaseDetails: CaseDetails | undefined) => void;
  setPreviousCaseDetails: (previousCaseDetails: CaseDetails | undefined) => void;
  setUpdatedDraftNote: (updDraftNote: CaseNote | undefined) => void;
  setPreviousDraftNote: (prevDraftNote: CaseNote | undefined) => void;
  setDraftCaseChanged: (caseChanged: boolean) => void;
  getUpdatedCaseDetails: () => CaseDetails | undefined;
  getPreviousCaseDetails: () => CaseDetails | undefined;
  getUpdatedDraftNote: () => CaseNote | undefined;
  getPreviousDraftNote: () => CaseNote | undefined;
  isDraftChanged: () => boolean | false;
}

const EconsultCaseDetailsView: FC<EconsultCaseDetailsViewProps> = ({
  setUpdatedCaseDetails,
  setUpdatedDraftNote,
  setPreviousCaseDetails,
  setPreviousDraftNote,
  setDraftCaseChanged,
  getUpdatedCaseDetails,
  getPreviousCaseDetails,
  getUpdatedDraftNote,
  getPreviousDraftNote,
  isDraftChanged
}) => {
  const { t } = useTranslation();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const {
    setCaseDetails,
    caseDetails,
    setDraftCaseSaved,
    draftNote,
    setDraftNote,
    setAssignerPrograms,
    userRole,
    setUserRole,
    abortAllUploads
  } = useEconsultCaseDetailsContext();
  const { caseId, providerType, statusType } = useParams();
  const navigate = useNavigate();
  const { data: caseDetailsResponse, isLoading: isCaseDetailsLoading } = useCaseDetails(
    caseId ? parseInt(caseId) : undefined,
    (errorDetails: any) => {
      removeAllErrorNotifications();
      addNotification({
        id: caseId ? parseInt(caseId) : 1,
        type: 'error',
        message: t('Case.consult.errorLoadingCase'),
        solution: t('Case.consult.errorLoadingCaseSolution') as string,
        details: JSON.stringify(errorDetails?.response)
      });
    }
  );
  const { data: programs } = useAssignerPrograms();
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const { data: caseResultDetails } = useCaseSearch(!userRole && !!caseId, caseId, 1, 1);

  useEffect(() => {
    if (caseDetailsResponse?.data) {
      setCaseDetails(caseDetailsResponse.data);
    } else {
      setCaseDetails(undefined);
    }
  }, [caseDetailsResponse, setCaseDetails]);

  useEffect(() => {
    if (
      caseResultDetails &&
      caseResultDetails.data &&
      caseResultDetails.data.length > 0 &&
      caseResultDetails.data[0].userRole
    ) {
      setUserRole(caseResultDetails.data[0].userRole);
    }
  }, [caseResultDetails, setUserRole]);

  useEffect(() => {
    setAssignerPrograms(programs);
  }, [programs, setAssignerPrograms]);

  const handleGoBack = () => {
    if (caseDetails && isDraftChanged()) {
      setIsDraftWarningDialogOpen(true);
    } else {
      navigate(`/case/${providerType}/${statusType}`);
    }
  };

  useEffect(() => {
    const existingUpdated = getUpdatedCaseDetails();
    setUpdatedCaseDetails({
      ...caseDetails,
      ...(existingUpdated?.externalReferralContent
        ? {
            externalReferralContent: existingUpdated.externalReferralContent,
            externalReferralId: existingUpdated.externalReferralId,
            externalReferralName: existingUpdated.externalReferralName
          }
        : {})
    });
    const prevCase = getPreviousCaseDetails();
    if (caseDetails && (!prevCase || prevCase.caseId !== caseDetails.caseId)) {
      setPreviousCaseDetails(JSON.parse(JSON.stringify(caseDetails)));
    }
    const updCaseDetails = getUpdatedCaseDetails();
    if (updCaseDetails && updCaseDetails.statusCd && updCaseDetails.statusCd === 'DRAFT') {
      if (hasCaseChanged(updCaseDetails, getPreviousCaseDetails())) {
        setDraftCaseSaved(false);
      }
      setUpdatedDraftNote(undefined);
    }
  }, [caseDetails]);

  useEffect(() => {
    setUpdatedDraftNote(draftNote);
    if (getUpdatedDraftNote() && getPreviousDraftNote()) {
      if (hasNoteChanged(getPreviousDraftNote(), getUpdatedDraftNote())) {
        setDraftCaseSaved(false);
      } else {
        setDraftCaseSaved(true);
      }
    } else if (!getPreviousDraftNote()) {
      setDraftCaseSaved(false);
    }
  }, [draftNote]);

  useEffect(() => {
    return () => {
      setDraftCaseSaved(false);
    };
  }, []);

  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    setUpdatedDraftNote(undefined);
    setUpdatedCaseDetails(undefined);
    setDraftNote(undefined);
    abortAllUploads();
    navigate(`/case/${providerType}/${statusType}`);
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
  };

  useEffect(() => {
    if (userRole === EconsultUserRoles.triage && caseDetails?.statusCd === NoteStatusCodes.draft) {
      removeAllErrorNotifications();
      addNotification({
        type: 'error',
        message: t('Case.consult.errorLoadingCase'),
        solution: t('Case.consult.errorLoadingCaseSolution') as string,
        details: `${t('Case.error.triageDraft')} ${caseDetails?.caseId}`
      });
    }
  }, [userRole, caseDetails?.statusCd]);

  if (userRole === EconsultUserRoles.triage && caseDetails?.statusCd === NoteStatusCodes.draft) {
    return null;
  }

  if (!caseDetails && isCaseDetailsLoading) {
    return <LoadingInProgress />;
  }

  return (
    <>
      <DisplayMobile>
        <Box mt={2}>
          <ArrowBackIcon onClick={handleGoBack} />
        </Box>
      </DisplayMobile>
      {caseDetails?.statusCd?.toUpperCase() === NoteActionTypes.draft ? (
        <EconsultCaseDraftDetailsView
          getUpdatedCaseDetails={getUpdatedCaseDetails}
          getPreviousCaseDetails={getPreviousCaseDetails}
          setPreviousCaseDetails={setPreviousCaseDetails}
          setUpdatedCaseDetails={setUpdatedCaseDetails}
          setDraftCaseChanged={setDraftCaseChanged}
        />
      ) : (
        <EconsultCaseConsultDetailsView
          setPreviousDraftNote={setPreviousDraftNote}
          getPreviousDraftNote={getPreviousDraftNote}
          isDraftChanged={isDraftChanged}
        />
      )}
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
    </>
  );
};

export default EconsultCaseDetailsView;
